/**
 * Button Trigger
 *
 * Opens popup when a button/link matching the selector is clicked.
 * Smart preloading: preloads when mouse gets close to the button.
 */

import type { Popup } from '../popup';
import { ProximityPreloadStrategy } from '../preload-strategies/proximity-strategy';
import { createLogger } from '../../shared/logger';
import { TriggerStateBroadcaster } from './trigger-state-broadcaster';

const logger = createLogger('ButtonTrigger');

export class ButtonTrigger {
	private popup: Popup;
	private selector: string;
	private boundHandler: (event: Event) => void;
	private boundPopupClosedHandler: (event: Event) => void;
	private preloadStrategy: ProximityPreloadStrategy | null = null;
	private broadcaster: TriggerStateBroadcaster;

	constructor(popup: Popup, selector: string) {
		logger.log('constructor - selector:', selector);
		this.popup = popup;
		this.selector = selector;
		this.boundHandler = this.handleClick.bind(this);
		this.boundPopupClosedHandler = this.handlePopupClosed.bind(this);
		this.broadcaster = new TriggerStateBroadcaster(
			popup.getFeedId(),
			'button'
		);
	}

	/**
	 * Initialize the trigger
	 */
	public init(): void {
		logger.log('init() called');

		if (!this.selector) {
			logger.log('No selector provided, skipping');
			return;
		}

		// Broadcast initial waiting state with selector info
		this.broadcaster.setState('waiting');
		this.broadcaster.setProgress({ selector: this.selector });

		// Set up proximity-based preloading (100px from button)
		this.preloadStrategy = new ProximityPreloadStrategy(this.selector, 100);
		this.preloadStrategy.init(() => {
			logger.log('Proximity preload triggered');
			this.broadcaster.setState('preloading');
			this.popup.preload();
		});

		// Use event delegation for dynamically added elements
		document.addEventListener('click', this.boundHandler);

		// Listen for popup close to reset preload strategy (for demo/testing)
		// Only if respectDismissal is false (dismissal not remembered)
		if (!this.popup.getRespectsDismisal()) {
			document.addEventListener(
				'gp_popup_closed',
				this.boundPopupClosedHandler
			);
		}
	}

	/**
	 * Destroy the trigger
	 */
	public destroy(): void {
		logger.log('destroy() called');
		this.broadcaster.destroy();
		document.removeEventListener('click', this.boundHandler);
		document.removeEventListener(
			'gp_popup_closed',
			this.boundPopupClosedHandler
		);
		this.preloadStrategy?.destroy();
	}

	/**
	 * Handle click events
	 */
	private handleClick(event: Event): void {
		const target = event.target as HTMLElement;

		// Check if clicked element or any parent matches the selector
		const triggerElement = target.closest(this.selector);

		if (triggerElement) {
			logger.log('Button clicked! Showing popup');
			event.preventDefault();
			this.broadcaster.setState('triggered');
			this.popup.show();
		}
	}

	/**
	 * Handle popup closed event - reset preload strategy for re-preloading
	 */
	private handlePopupClosed(event: Event): void {
		const customEvent = event as CustomEvent;
		// Only handle events for our popup
		if (customEvent.detail?.feedId !== this.popup.getFeedId()) {
			return;
		}

		logger.log(
			'Popup closed, resetting preload strategy for re-preloading'
		);
		this.preloadStrategy?.reset();
		this.broadcaster.setState('waiting');
	}
}
